import { streamEvents } from "../client.js";
import { refreshThreadComponent } from "./snapshot.js";
import { activeThreadTurnFromState, latestThreadSeq, syncTurnStateFromEvent } from "./state.js";

export const threadLiveDeps = {
  streamEvents,
  refreshThreadComponent,
  setTimeout: globalThis.setTimeout.bind(globalThis),
  clearTimeout: globalThis.clearTimeout.bind(globalThis)
};

const threadSnapshotPollIntervalMs = 750;

export function stopThreadComponentLive(state, deps = threadLiveDeps) {
  if (state.live.snapshotTimer) {
    deps.clearTimeout(state.live.snapshotTimer);
    state.live.snapshotTimer = null;
  }
  if (state.live.pollTimer) {
    deps.clearTimeout(state.live.pollTimer);
    state.live.pollTimer = null;
  }
  if (state.live.streamAbort) {
    state.live.streamAbort.abort();
    state.live.streamAbort = null;
  }
}

export function buildEventStreamPath(state, turnID) {
  const since = latestThreadSeq(state, turnID);
  const query = since > 0 ? `?stream=1&since=${encodeURIComponent(String(since))}` : "?stream=1";
  return `/v1/turns/${encodeURIComponent(turnID)}/events${query}`;
}

export function scheduleThreadSnapshotRefresh(state, apiKey, threadID, options, deps = threadLiveDeps, delay = 250) {
  if (state.live.snapshotTimer) {
    deps.clearTimeout(state.live.snapshotTimer);
  }
  state.live.snapshotTimer = deps.setTimeout(() => {
    state.live.snapshotTimer = null;
    void deps.refreshThreadComponent(state, apiKey, threadID, {
      request: options.request,
      includeDiagnostics: options.includeDiagnostics
    }).then(() => {
      if (activeThreadTurnFromState(state)) {
        scheduleThreadSnapshotPoll(state, apiKey, threadID, options, deps);
      } else if (state.live.pollTimer) {
        deps.clearTimeout(state.live.pollTimer);
        state.live.pollTimer = null;
      }
      options.render?.();
    }).catch((error) => {
      options.setStatus?.(error.message, "error");
      options.render?.();
    });
  }, delay);
}

export function needsSnapshotRefresh(eventType) {
  return eventType === "turn.started" ||
    eventType === "turn.completed" ||
    eventType === "turn.failed" ||
    eventType === "turn.canceled" ||
    eventType === "turn.artifact" ||
    eventType === "turn.diff" ||
    eventType === "turn.decision.requested" ||
    eventType === "turn.decision.updated" ||
    eventType === "turn.decision.resolved" ||
    eventType === "turn.decision.expired" ||
    eventType === "turn.decision.failed";
}

export function mergeThreadEvent(state, turnID, next) {
  if (!next) {
    return false;
  }
  if (!state.eventsByTurnID[turnID]) {
    state.eventsByTurnID[turnID] = [];
  }
  if (!state.eventSeqsByTurnID[turnID]) {
    state.eventSeqsByTurnID[turnID] = new Set();
  }
  if (state.eventSeqsByTurnID[turnID].has(next.seq)) {
    return false;
  }
  state.eventSeqsByTurnID[turnID].add(next.seq);
  state.eventsByTurnID[turnID] = [...state.eventsByTurnID[turnID], next].sort((left, right) => left.seq - right.seq);
  state.streamState = "connected";
  syncTurnStateFromEvent(state, turnID, next);
  return true;
}

export function scheduleThreadSnapshotPoll(state, apiKey, threadID, options, deps) {
  if (state.live.pollTimer) {
    return;
  }
  if (!activeThreadTurnFromState(state)) {
    return;
  }
  state.live.pollTimer = deps.setTimeout(() => {
    state.live.pollTimer = null;
    void deps.refreshThreadComponent(state, apiKey, threadID, {
      request: options.request,
      includeDiagnostics: options.includeDiagnostics
    }).then(() => {
      if (activeThreadTurnFromState(state)) {
        scheduleThreadSnapshotPoll(state, apiKey, threadID, options, deps);
      }
      options.render?.();
    }).catch((error) => {
      options.setStatus?.(error.message, "error");
      options.render?.();
    });
  }, threadSnapshotPollIntervalMs);
}
